import { z } from 'zod';
import { DM_SANS_FONT_FAMILY } from '@terra/shared-utils';

// Re-export the PdfTemplate schema from orchestrator
export { PdfTemplateSchema } from '@terra/shared-schemas-orchestrator';

// PDF Template DTOs
export const CreatePdfTemplateSchema = z.object({
  name: z.string().min(1, 'Template name is required'),
  description: z.string().optional(),
  logoS3Key: z.string().optional(),
  primaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .default('#1a365d'),
  secondaryColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .default('#2c5282'),
  accentColor: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .default('#3182ce'),
  fontFamily: z.string().default(DM_SANS_FONT_FAMILY),
  fontS3Key: z.string().optional(),
  headerContent: z.record(z.string(), z.unknown()).optional(),
  footerContent: z.record(z.string(), z.unknown()).optional(),
  sections: z.array(z.string()).default(['cover', 'toc', 'findings']),
  isDefault: z.boolean().default(false),
  requirePassword: z.boolean().default(false),
});

export const UpdatePdfTemplateSchema = CreatePdfTemplateSchema.partial().extend(
  {
    templateId: z.string(),
  },
);

export const PdfTemplateIdSchema = z.object({
  templateId: z.string(),
});

// PDF Generation DTOs
export const PdfJobStatusSchema = z.enum([
  'PENDING',
  'PREPARING',
  'FETCHING',
  'RENDERING',
  'UPLOADING',
  'COMPLETED',
  'FAILED',
]);

export const PdfJobSchema = z.object({
  id: z.string(),
  reportId: z.string(),
  templateId: z.string(),
  status: PdfJobStatusSchema,
  progress: z.number().min(0).max(100),
  message: z.string(),
  downloadUrl: z.string().optional(),
  error: z.string().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const GeneratePdfRequestSchema = z.object({
  reportId: z.string(),
  templateId: z.string(),
  findingIds: z.array(z.string()),
  cacheKey: z.string().optional(),
  options: z
    .object({
      includeScreenshots: z.boolean().default(true),
      includeRemediation: z.boolean().default(false),
      includeFindingsHighLevelOverview: z.boolean().default(false),
      passwordProtect: z.boolean().default(false),
      password: z.string().optional(),
    })
    .optional(),
});

export const GeneratePdfResponseSchema = z.object({
  jobId: z.string(),
  status: PdfJobStatusSchema,
  message: z.string(),
  sseEndpoint: z.string(),
});

export const PdfProgressEventSchema = z.object({
  jobId: z.string(),
  status: PdfJobStatusSchema,
  progress: z.number(),
  message: z.string(),
  downloadUrl: z.string().optional(),
  error: z.string().optional(),
});

// ==================== Findings Export (Streaming Download) ====================

export const DownloadFindingsPdfSelectionSchema = z.object({
  allFindings: z.literal(true),
  exceptFindingIds: z.array(z.string()).max(5000).default([]),
  query: z.record(z.string(), z.unknown()),
});

export const DownloadFindingsPdfRequestSchema = z
  .object({
    findingIds: z.array(z.string()).optional(),
    selection: DownloadFindingsPdfSelectionSchema.optional(),
    applicationId: z.string().optional(),
    title: z.string().optional(),
    jobId: z.string().optional(),
    options: z
      .object({
        includeScreenshots: z.boolean().default(true),
        includeRemediation: z.boolean().default(false),
      })
      .optional(),
  })
  .refine(
    (request) =>
      (request.findingIds?.length ?? 0) > 0 || request.selection !== undefined,
    { message: 'findingIds or selection is required' },
  );

export const DownloadFindingsPdfResponseSchema = z.object({
  success: z.boolean(),
  error: z.string().optional(),
});

// Type exports
export type CreatePdfTemplate = z.infer<typeof CreatePdfTemplateSchema>;
export type UpdatePdfTemplate = z.infer<typeof UpdatePdfTemplateSchema>;
export type PdfJobStatus = z.infer<typeof PdfJobStatusSchema>;
export type PdfJob = z.infer<typeof PdfJobSchema>;
export type GeneratePdfRequest = z.infer<typeof GeneratePdfRequestSchema>;
export type GeneratePdfResponse = z.infer<typeof GeneratePdfResponseSchema>;
export type PdfProgressEvent = z.infer<typeof PdfProgressEventSchema>;
export type DownloadFindingsPdfRequest = z.infer<
  typeof DownloadFindingsPdfRequestSchema
>;
export type DownloadFindingsPdfResponse = z.infer<
  typeof DownloadFindingsPdfResponseSchema
>;
